import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ThemeContextType } from '../types';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<'light' | 'dark'>('dark');

  // Load theme from localStorage or default to dark
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (savedTheme) {
      setThemeState(savedTheme);
    } else {
      setThemeState('dark'); // default to dark
    }
  }, []);

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;

    // Remove old theme classes
    root.classList.remove('light', 'dark');

    // Add current theme class
    root.classList.add(theme);

    // Update CSS variables based on theme
    if (theme === 'dark') {
      root.style.setProperty('--current-bg-primary', '#1a1a1a');
      root.style.setProperty('--current-bg-secondary', '#2a2a2a');
      root.style.setProperty('--current-bg-tertiary', '#3a3a3a');
      root.style.setProperty('--current-text-primary', '#f0f0f0');
      root.style.setProperty('--current-text-secondary', '#d0d0d0');
      root.style.setProperty('--current-text-muted', '#a0a0a0');
      root.style.setProperty('--current-accent-primary', '#bb86fc');
      root.style.setProperty('--current-accent-secondary', '#c5a6ff');
      root.style.setProperty('--current-accent-hover', '#9a67ea');
      root.style.setProperty('--current-border-primary', '#4a4a4a');
    } else {
      root.style.setProperty('--current-bg-primary', '#ffffff');
      root.style.setProperty('--current-bg-secondary', '#f8fafc');
      root.style.setProperty('--current-bg-tertiary', '#e2e8f0');
      root.style.setProperty('--current-text-primary', '#0f172a');
      root.style.setProperty('--current-text-secondary', '#475569');
      root.style.setProperty('--current-text-muted', '#64748b');
      root.style.setProperty('--current-accent-primary', '#4f46e5');
      root.style.setProperty('--current-accent-secondary', '#6366f1');
      root.style.setProperty('--current-accent-hover', '#4338ca');
      root.style.setProperty('--current-border-primary', '#cbd5e1');
    }

    // Store theme in localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const value = {
    theme,
    toggleTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};